import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import clsx from "clsx";
import { ComponentProps } from "react";

import { getInitials } from "../../utils/names";
import { Tooltip } from "../ui-kit/base/tooltip";
import { resolveEnumLabel } from "../ui-kit/fields/utils";
import { MonitoringKind, MonitoringUserInfo } from "./monitoring-assignments";

export function MonitoringUserBubbles({
  users,
}: {
  users: MonitoringUserInfo[];
}) {
  const { t } = useLingui();
  if (users.length === 0) return null;

  return (
    <span
      aria-label={t`Monitoring users`}
      className="inline-flex shrink-0 items-center -space-x-1.5 align-middle"
    >
      {users.map((user) => (
        <Tooltip
          key={user.uuid}
          content={
            <>
              <div className="font-medium">{user.username}</div>
              <div className="text-muted-foreground">
                {resolveEnumLabel(monitoringKindLabels[user.kind])}
              </div>
            </>
          }
        >
          <MonitoringUserBubble user={user} className="cursor-help" />
        </Tooltip>
      ))}
    </span>
  );
}

const monitoringKindLabels: Record<MonitoringKind, MessageDescriptor> = {
  medical: msg`Medical monitoring`,
  technical: msg`Technical monitoring`,
};

/**
 * Initials bubble, tinted by monitoring kind. The kind is also spelled out
 * in the bubble's tooltip and the filter options, so color is a redundant
 * cue, not the only one.
 */
const kindClasses: Record<MonitoringKind, string> = {
  medical: "bg-info-soft border-info-border text-info-foreground",
  technical: "bg-muted border-border text-muted-foreground",
};

const bubbleClasses =
  "inline-flex size-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-medium select-none";

export function MonitoringUserBubble({
  user,
  className,
  ...props
}: {
  user: MonitoringUserInfo;
  className?: string;
} & ComponentProps<"span">) {
  return (
    <span
      className={clsx(bubbleClasses, kindClasses[user.kind], className)}
      {...props}
    >
      {getInitials(user.username)}
    </span>
  );
}
